import { FC, useCallback } from 'react';
import { useDropzone, DropzoneOptions } from 'react-dropzone';
import { Box, Typography, Paper, IconButton, Chip, Alert } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import { Controller, useFormContext } from 'react-hook-form';

export interface FormFileUploadProps {
    name: string;
    label: string;
    helperText?: string;
    required?: boolean;
    disabled?: boolean;
    accept?: Record<string, string[]>;
    maxSize?: number; // in bytes
    maxFiles?: number;
    multiple?: boolean;
    showPreview?: boolean;
    convertToBase64?: boolean; // Convert files to base64
    processImage?: (file: File) => Promise<string>; // Custom image processor
}

export const FormFileUpload: FC<FormFileUploadProps> = ({
    name,
    label,
    helperText,
    required = false,
    disabled = false,
    accept = { 'image/*': ['.png', '.jpg', '.jpeg', '.gif'] },
    maxSize = 5242880, // 5MB
    maxFiles = 1,
    multiple = false,
    showPreview = true,
    convertToBase64 = false,
    processImage,
}) => {
    const {
        control,
        formState: { errors },
    } = useFormContext();

    const error = errors[name];
    const hasError = !!error;
    const errorMessage = error?.message as string;

    // Determine display name based on file type
    const getFileName = (file: any) => {
        return file.name || file.filename || 'Unknown file';
    };

    return (
        <Controller
            name={name}
            control={control}
            render={({ field: { onChange, value } }) => {
                const files = value || [];

                const onDrop = useCallback(
                    async (acceptedFiles: File[]) => {
                        if (convertToBase64 && processImage) {
                            // Convert files to base64 format
                            const processedFiles = await Promise.all(
                                acceptedFiles.map(async (file) => {
                                    try {
                                        const base64String = await processImage(file);
                                        return {
                                            name: file.name,
                                            image_base64: base64String,
                                        };
                                    } catch (error) {
                                        console.error('Error processing file:', error);
                                        return null;
                                    }
                                })
                            );

                            const validFiles = processedFiles.filter(Boolean);

                            if (multiple) {
                                onChange([...files, ...validFiles]);
                            } else {
                                onChange(validFiles);
                            }
                        } else {
                            // Keep as File objects
                            if (multiple) {
                                onChange([...files, ...acceptedFiles]);
                            } else {
                                onChange(acceptedFiles);
                            }
                        }
                    },
                    // eslint-disable-next-line react-hooks/exhaustive-deps
                    [files, multiple, onChange]
                );

                const removeFile = (index: number) => {
                    const newFiles = [...files];
                    newFiles.splice(index, 1);
                    onChange(newFiles);
                };

                const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
                    onDrop,
                    accept,
                    maxSize,
                    maxFiles,
                    multiple,
                    disabled,
                } as DropzoneOptions);

                return (
                    <Box>
                        <Typography
                            variant="body2"
                            sx={{
                                mb: 1,
                                fontWeight: 500,
                                color: hasError ? 'error.main' : 'text.primary',
                            }}
                        >
                            {label}
                            {required && (
                                <Typography component="span" color="error" sx={{ ml: 0.5 }}>
                                    *
                                </Typography>
                            )}
                        </Typography>

                        <Paper
                            {...getRootProps()}
                            elevation={0}
                            sx={{
                                p: 3,
                                border: '2px dashed',
                                borderColor: hasError
                                    ? 'error.main'
                                    : isDragActive
                                    ? 'primary.main'
                                    : 'divider',
                                backgroundColor: isDragActive
                                    ? 'action.hover'
                                    : disabled
                                    ? 'action.disabledBackground'
                                    : 'background.paper',
                                cursor: disabled ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    borderColor: disabled ? 'divider' : 'primary.main',
                                    backgroundColor: disabled ? 'action.disabledBackground' : 'action.hover',
                                },
                            }}
                        >
                            <input {...getInputProps()} />
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 1,
                                }}
                            >
                                <CloudUploadIcon
                                    sx={{
                                        fontSize: 48,
                                        color: hasError ? 'error.main' : 'text.secondary',
                                    }}
                                />
                                <Typography variant="body2" color="text.secondary" align="center">
                                    {isDragActive
                                        ? 'Drop the files here...'
                                        : 'Drag & drop files here, or click to select files'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {`Max size: ${(maxSize / 1024 / 1024).toFixed(0)}MB${
                                        multiple ? `, Max files: ${maxFiles}` : ''
                                    }`}
                                </Typography>
                            </Box>
                        </Paper>

                        {/* File Rejections */}
                        {fileRejections.length > 0 && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {fileRejections.map(({ file, errors }) => (
                                    <Box key={file.name}>
                                        <Typography variant="body2">
                                            {file.name}:{' '}
                                            {errors.map((e) => e.message).join(', ')}
                                        </Typography>
                                    </Box>
                                ))}
                            </Alert>
                        )}

                        {/* Uploaded Files */}
                        {files.length > 0 && (
                            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {files.map((file: any, index: number) => (
                                    <Chip
                                        key={index}
                                        icon={<ImageIcon />}
                                        label={getFileName(file)}
                                        onDelete={() => removeFile(index)}
                                        deleteIcon={<DeleteIcon />}
                                        variant="outlined"
                                        sx={{
                                            maxWidth: '100%',
                                            '& .MuiChip-label': {
                                                maxWidth: '200px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            },
                                        }}
                                    />
                                ))}
                            </Box>
                        )}

                        {/* Preview */}
                        {showPreview && files.length > 0 && !convertToBase64 && (
                            <Box
                                sx={{
                                    mt: 2,
                                    display: 'grid',
                                    gridTemplateColumns: {
                                        xs: 'repeat(2, 1fr)',
                                        sm: 'repeat(3, 1fr)',
                                        md: 'repeat(4, 1fr)',
                                    },
                                    gap: 2,
                                }}
                            >
                                {files.map((file: File, index: number) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            position: 'relative',
                                            aspectRatio: '1',
                                            borderRadius: 1,
                                            overflow: 'hidden',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                        }}
                                    >
                                        <img
                                            src={URL.createObjectURL(file)}
                                            alt={file.name}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                            }}
                                        />
                                        <IconButton
                                            size="small"
                                            onClick={() => removeFile(index)}
                                            sx={{
                                                position: 'absolute',
                                                top: 4,
                                                right: 4,
                                                backgroundColor: 'background.paper',
                                                '&:hover': {
                                                    backgroundColor: 'error.main',
                                                    color: 'error.contrastText',
                                                },
                                            }}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Box>
                        )}

                        {/* Helper/Error Text */}
                        {(hasError || helperText) && (
                            <Typography
                                variant="caption"
                                color={hasError ? 'error' : 'text.secondary'}
                                sx={{ mt: 1, display: 'block' }}
                            >
                                {hasError ? errorMessage : helperText}
                            </Typography>
                        )}
                    </Box>
                );
            }}
        />
    );
};

